import { css } from '@emotion/react';
import {
  Box,
  COLOR_PALETTE,
  Flex,
  OptimizeImage,
  Stack,
  Text,
  Title,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { Trans, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';
import { NEXT_PUBLIC_CDN_URL } from '../../../../appConfig';

const styleDescText = css({
  fontSize: 18,
  fontWeight: 500,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 14,
  },
});

const AdvantagesSection = () => {
  const { t } = useTranslation();

  const ADVANTAGES: { bgColor: string; title: string; description: ReactNode; imageUrl: string }[] =
    [
      {
        bgColor: COLOR_PALETTE.yellow[0],
        title: t('open.knowledge.advantage.revenue.title'),
        description: (
          <>
            <Text component="span" color="gray.7" css={styleDescText}>
              <Trans
                i18nKey="open.knowledge.advantage.revenue.description"
                components={{ br: <br /> }}
              />
            </Text>
          </>
        ),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/3d_icon_money_pocket.png`,
      },
      {
        bgColor: COLOR_PALETTE.indigo[0],
        title: t('open.knowledge.advantage.ai.title'),
        description: (
          <>
            <Text component="span" color="gray.7" css={styleDescText}>
              <Trans
                i18nKey="open.knowledge.advantage.ai.description"
                components={{ br: <br /> }}
              />
            </Text>
          </>
        ),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/3d_icon_magic.png`,
      },
      {
        bgColor: COLOR_PALETTE.cyan[0],
        title: t('open.knowledge.advantage.global.title'),
        description: (
          <>
            <Text component="span" color="gray.7" css={styleDescText}>
              <Trans
                i18nKey="open.knowledge.advantage.global.description"
                components={{ br: <br /> }}
              />
            </Text>
          </>
        ),
        imageUrl: `${NEXT_PUBLIC_CDN_URL}/assets/images/open-knowledge/3d_icon_map.png`,
      },
    ];

  return (
    <Box component="section" css={styleSection}>
      <Flex direction="column" align="center" css={styleContainer}>
        <Stack spacing={40} align="center" css={styleContents}>
          <Title order={2} align="center" color="dark.9" css={styleSectionTitle}>
            <Trans i18nKey="open.knowledge.advantage.title" components={{ br: <br /> }} />
          </Title>
          <Box css={styleInnerContainer}>
            <Stack css={styleCards}>
              {ADVANTAGES.map((advantage) => (
                <Flex key={advantage.title} css={styleCard(advantage.bgColor)}>
                  <Box css={styleCardImage}>
                    <OptimizeImage src={advantage.imageUrl} w={100 * 2} css={styleImage} />
                  </Box>
                  <Stack spacing={12} css={styleCardTextContent}>
                    <Text color="gray.9" css={styleCardTitle}>
                      {advantage.title}
                    </Text>
                    <Box css={styleCardDesc}>{advantage.description}</Box>
                  </Stack>
                </Flex>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Flex>
    </Box>
  );
};

export default AdvantagesSection;

const styleSection = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#ffffff',
  padding: '100px 0',
  width: '100%',
  [desktopFirstMediaQuery.mobile]: {
    padding: '80px 0',
  },
});

const styleContainer = css({
  maxWidth: 1200,
  width: '100%',
  padding: '0 32px',
  [desktopFirstMediaQuery.mobile]: {
    padding: '0 16px',
  },
});

const styleContents = css({
  maxWidth: 1200,
  width: '100%',
});

const styleSectionTitle = css({
  fontSize: 34,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 24,
  },
});

const styleInnerContainer = css({
  width: '100%',
});

const styleCards = css({
  maxWidth: 1200,
  width: '100%',
  gap: 24,
  [desktopFirstMediaQuery.mobile]: {
    gap: 8,
  },
});

const styleCard = (bgColor: string) =>
  css({
    position: 'relative',
    backgroundColor: bgColor,
    borderRadius: 16,
    padding: '24px 40px',
    gap: 12,
    overflow: 'hidden',
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    [desktopFirstMediaQuery.mobile]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: 20,
    },
  });

const styleCardImage = css({
  flexShrink: 0,
  order: 1,
  [desktopFirstMediaQuery.mobile]: {
    order: -1,
  },
});

const styleImage = css({
  width: 100,
  height: 100,
  [desktopFirstMediaQuery.mobile]: {
    width: 48,
    height: 48,
  },
});

const styleCardTextContent = css({
  order: 0,
});

const styleCardTitle = css({
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1.3,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 16,
  },
});

const styleCardDesc = css({
  fontSize: 18,
  lineHeight: 1.5,
  [desktopFirstMediaQuery.mobile]: {
    fontSize: 14,
  },
});
